import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import '../styles/UpdateBanner.css';

// Détection de nouvelle version : à l'arrivée sur le site on mémorise
// l'index.html servi (il référence tous les fichiers hachés du build : il
// change donc à CHAQUE déploiement front, même minime). On le re-télécharge
// périodiquement : s'il diffère de la référence, un déploiement a eu lieu →
// on propose de recharger. Aucune déconnexion, aucun rechargement forcé.
// Le bundle principal (main.abc123.js) sert de second signal, utile si la
// page chargée était déjà en retard d'une version au moment de l'arrivée.
const BUNDLE_RE = /static\/js\/main\.[0-9a-f]+\.js/;
const CHECK_INTERVAL_MS = 60 * 1000;
// « Plus tard » = pause de 15 min, puis le bandeau revient (sans son) tant
// que la page n'a pas été mise à jour. Jamais définitif : sinon un clic
// réflexe sur la croix laisserait l'utilisateur sur une vieille version.
const SNOOZE_MS = 15 * 60 * 1000;

const currentBundle = () => {
    const src = Array.from(document.querySelectorAll('script[src]'))
        .map((s) => s.getAttribute('src') || '')
        .find((s) => BUNDLE_RE.test(s));
    return src ? src.match(BUNDLE_RE)[0] : null;
};

/** Petit carillon doux à deux notes montantes, plus discret que les annonces. */
function playUpdateChime() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
        [659.25, 987.77].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t0 = ctx.currentTime + i * 0.14;
            gain.gain.setValueAtTime(0, t0);
            gain.gain.linearRampToValueAtTime(0.1, t0 + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.4);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t0);
            osc.stop(t0 + 0.45);
        });
        setTimeout(() => ctx.close().catch(() => {}), 1200);
    } catch {
        // pas de son : le bandeau suffit
    }
}

const UpdateBanner = () => {
    const [available, setAvailable] = useState(false);
    const [snoozed, setSnoozed] = useState(false);
    const snoozeTimerRef = useRef(null);

    const snooze = () => {
        setSnoozed(true);
        clearTimeout(snoozeTimerRef.current);
        snoozeTimerRef.current = setTimeout(() => setSnoozed(false), SNOOZE_MS);
    };

    useEffect(() => () => clearTimeout(snoozeTimerRef.current), []);

    // Son joué une seule fois, à la première apparition du bandeau
    // (pas de nouveau son quand il revient après un « plus tard »).
    useEffect(() => {
        if (available) playUpdateChime();
    }, [available]);

    useEffect(() => {
        // En dev, le build n'est pas haché : rien à surveiller.
        if (process.env.NODE_ENV !== 'production') return undefined;
        const mine = currentBundle();

        let stopped = false;
        let baseline = null;

        const check = async () => {
            try {
                const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
                if (!res.ok || stopped) return;
                const html = await res.text();
                if (stopped) return;

                // 1re réponse réussie = référence de la version actuelle.
                if (baseline === null) {
                    baseline = html;
                    // Cas limite : la page chargée était déjà en retard d'une version.
                    const server = (html.match(BUNDLE_RE) || [])[0] || null;
                    if (mine && server && server !== mine) setAvailable(true);
                    return;
                }

                if (html !== baseline) {
                    // Trace volontaire : facilite le diagnostic en production.
                    console.info('[SPOT] Nouvelle version du site détectée');
                    setAvailable(true);
                }
            } catch { /* hors ligne / erreur réseau : on réessaiera */ }
        };

        check(); // capture immédiate de la référence
        const iv = setInterval(check, CHECK_INTERVAL_MS);
        // Retour sur l'onglet OU retour de focus sur la fenêtre : contrôle immédiat.
        const onVisible = () => { if (document.visibilityState === 'visible') check(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            stopped = true;
            clearInterval(iv);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, []);

    if (!available || snoozed) return null;

    return (
        <div className="upb" role="alert">
            <span className="upb-icon"><RefreshCw size={15} /></span>
            <div className="upb-text">
                <strong>Nouvelle version disponible</strong>
                <span>Rechargez pour profiter des dernières améliorations.</span>
            </div>
            <button type="button" className="upb-btn" onClick={() => window.location.reload()}>
                Mettre à jour
            </button>
            <button
                type="button"
                className="upb-close"
                onClick={snooze}
                aria-label="Plus tard"
                title="Plus tard"
            >
                <X size={14} />
            </button>
        </div>
    );
};

export default UpdateBanner;
